import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Camera, 
  Radio, 
  Router, 
  Volume2,
  Monitor,
  Square,
  Plus,
  Edit,
  Trash2,
  X,
  Package,
  RefreshCw,
  MapPin,
  Tags
} from 'lucide-react';
import { notifications, showSuccess, showError } from '../utils/notifications';
import ConfirmDialog from '../components/ConfirmDialog';
import XTAMSpinner from '../components/XTAMSpinner';
import './InventoryPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

type DeviceType = 'cameras' | 'buttons' | 'miniot' | 'routers' | 'sirenas' | 'xtam_video' | 'catalogos';
type CatalogDeviceType = 'cameras' | 'buttons' | 'routers' | 'sirenas' | 'xtam_video' | 'miniot';
type CatalogType = 'brands' | 'models';

interface Camera {
  id?: number;
  serial: string;
  plate: string;
  id_brand: number;
  id_model: number;
  isActive: boolean;
}

interface Button {
  id?: number;
  serial_device: string;
  id_brand?: number;
  id_model?: number;
  description?: string;
  code?: string;
  inventory_state_id?: number;
  isActive: boolean;
}

interface MinIoT {
  id?: number;
  serial_device: string;
  id_model?: number;
  firmware?: string;
  inventory_state_id?: number;
}

interface Router {
  id?: number;
  serial_device: string;
  inventory_state_id?: number;
  isActive: boolean;
}

interface Sirena {
  id?: number;
  serial_device: string;
  inventory_state_id?: number;
}

interface XtamVideo {
  id?: number;
  serial_device: string;
  id_model?: number;
  inventory_state_id?: number;
  production_date?: string;
  instalation_date?: string;
  board_serial?: string;
  board_reference?: string;
  mac?: string;
  switch?: string;
  internal_dvr?: string;
  serial_disk_main?: string;
  serial_disk_recordings?: string;
  serial_ram?: string;
  serial_processor?: string;
  serial_source_power?: string;
  isActive: boolean;
}

type DeviceData = Camera | Button | MinIoT | Router | Sirena | XtamVideo;

const InventoryPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<DeviceType>('cameras');
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [deviceCounts, setDeviceCounts] = useState<Record<DeviceType, number>>({
    cameras: 0,
    buttons: 0,
    miniot: 0,
    routers: 0,
    sirenas: 0,
    xtam_video: 0,
    catalogos: 0
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceData | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<DeviceData | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filtros para inventario de cámaras
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'true' | 'false'>('all');

  // Datos de referencia para cámaras y botones
  const [brands, setBrands] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);

  // Catálogos (marcas y modelos)
  const [catalogDeviceType, setCatalogDeviceType] = useState<CatalogDeviceType>('cameras');
  const [catalogType, setCatalogType] = useState<CatalogType>('brands');
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingCatalogItem, setEditingCatalogItem] = useState<any | null>(null);
  const [catalogFormData, setCatalogFormData] = useState<any>({ name: '', description: '', versionHw: '', versionSf: '', so: '', isActive: true });
  const [showConfirmDeleteCatalog, setShowConfirmDeleteCatalog] = useState(false);
  const [catalogItemToDelete, setCatalogItemToDelete] = useState<any | null>(null);
  const [deletingCatalog, setDeletingCatalog] = useState(false);

  const tabs = [
    { id: 'cameras' as DeviceType, label: 'Cámaras', icon: Camera, color: '#A61B34' },
    { id: 'buttons' as DeviceType, label: 'Botones', icon: Square, color: '#4A90E2' },
    { id: 'miniot' as DeviceType, label: 'MinIoT', icon: Radio, color: '#7B68EE' },
    { id: 'routers' as DeviceType, label: 'Routers', icon: Router, color: '#50C878' },
    { id: 'sirenas' as DeviceType, label: 'Sirenas', icon: Volume2, color: '#FF6347' },
    { id: 'xtam_video' as DeviceType, label: 'Xtam Video', icon: Monitor, color: '#FFD700' },
    { id: 'catalogos' as DeviceType, label: 'Catálogos', icon: Tags, color: '#9B59B6' }
  ];

  useEffect(() => {
    setFilterBrand('');
    setFilterModel('');
    if (activeTab === 'catalogos') {
      loadCatalogItems();
    } else {
      loadAllCounts();
      loadDevices({ filterBrand: '', filterModel: '' });
      if (activeTab === 'cameras') {
        loadCameraReferences();
      } else if (activeTab === 'buttons') {
        loadButtonReferences();
      } else if (activeTab === 'routers') {
        loadRouterReferences();
      } else if (activeTab === 'sirenas') {
        loadSirenReferences();
      } else if (activeTab === 'xtam_video') {
        loadXtamVideoReferences();
      } else if (activeTab === 'miniot') {
        loadMiniotReferences();
      }
    }
  }, [activeTab, catalogDeviceType, catalogType]);

  useEffect(() => {
    if (activeTab === 'catalogos') {
      loadCatalogItems();
    }
  }, [catalogDeviceType, catalogType]);

  useEffect(() => {
    loadAllCounts();
    if (activeTab === 'cameras') {
      loadCameraReferences();
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadAllCounts = async () => {
    try {
      const headers = getAuthHeaders();
      const counts: Record<DeviceType, number> = {
        cameras: 0,
        buttons: 0,
        miniot: 0,
        routers: 0,
        sirenas: 0,
        xtam_video: 0,
        catalogos: 0
      };

      // Cargar el conteo de cada tipo de dispositivo
      const deviceTypes: DeviceType[] = ['cameras', 'buttons', 'miniot', 'routers', 'sirenas', 'xtam_video'];
      
      await Promise.all(
        deviceTypes.map(async (type) => {
          try {
            const endpoint = getEndpoint(type);
            const response = await axios.get(`${API_URL}${endpoint}`, { headers });
            counts[type] = response.data.data?.length || 0;
          } catch (error) {
            console.error(`Error loading count for ${type}:`, error);
            counts[type] = 0;
          }
        })
      );

      setDeviceCounts(counts);
    } catch (error) {
      console.error('Error loading device counts:', error);
    }
  };

  const loadCameraReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const [brandsRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/cameras/brands`, { headers }),
        axios.get(`${API_URL}/inventory/cameras/models`, { headers })
      ]);
      setBrands(brandsRes.data.data || []);
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading camera references:', error);
    }
  };

  const loadButtonReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const [brandsRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/buttons/brands`, { headers }),
        axios.get(`${API_URL}/inventory/buttons/models`, { headers })
      ]);
      setBrands(brandsRes.data.data || []);
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading button references:', error);
    }
  };

  const loadRouterReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const [brandsRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/routers/brands`, { headers }),
        axios.get(`${API_URL}/inventory/routers/models`, { headers })
      ]);
      setBrands(brandsRes.data.data || []);
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading router references:', error);
    }
  };

  const loadSirenReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const [brandsRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/sirens/brands`, { headers }),
        axios.get(`${API_URL}/inventory/sirens/models`, { headers })
      ]);
      setBrands(brandsRes.data.data || []);
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading siren references:', error);
    }
  };

  const loadXtamVideoReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const modelsRes = await axios.get(`${API_URL}/inventory/xtam-videos/models`, { headers });
      setBrands([]); // XTAM Video no tiene marcas
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading XTAM Video references:', error);
    }
  };

  const loadMiniotReferences = async () => {
    try {
      const headers = getAuthHeaders();
      const modelsRes = await axios.get(`${API_URL}/inventory/miniots/models`, { headers });
      setBrands([]); // MinIoT no tiene marcas
      setModels(modelsRes.data.data || []);
    } catch (error) {
      console.error('Error loading MinIoT references:', error);
    }
  };

  const getCatalogEndpoint = (deviceType: CatalogDeviceType, type: CatalogType) => {
    // El backend usa 'sirens' (EN), el frontend muestra 'sirenas' (ES)
    const apiDeviceType = deviceType === 'sirenas' ? 'sirens' : deviceType;
    // XTAM Video y MinIoT solo tienen modelos (sin marcas)
    if (deviceType === 'xtam_video') return '/inventory/xtam-videos/models';
    if (deviceType === 'miniot') return '/inventory/miniots/models';
    return `/inventory/${apiDeviceType}/${type}`;
  };

  const loadCatalogItems = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = getCatalogEndpoint(catalogDeviceType, catalogType);
      const response = await axios.get(`${API_URL}${endpoint}`, { headers });
      setCatalogItems(response.data.data || []);
    } catch (error) {
      console.error('Error loading catalog items:', error);
      setCatalogItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCatalogModal = (item?: any) => {
    if (item) {
      setEditingCatalogItem(item);
      setCatalogFormData({
        name: item.name || '',
        description: item.description || '',
        versionHw: item.versionHw || item.version_hw || '',
        versionSf: item.versionSf || item.version_sf || '',
        so: item.so || '',
        isActive: item.isActive ?? item.is_active ?? true
      });
    } else {
      setEditingCatalogItem(null);
      setCatalogFormData({ name: '', description: '', versionHw: '', versionSf: '', so: '', isActive: true });
    }
    setShowCatalogModal(true);
  };

  const handleCloseCatalogModal = () => {
    setShowCatalogModal(false);
    setEditingCatalogItem(null);
    setCatalogFormData({ name: '', description: '', versionHw: '', versionSf: '', so: '', isActive: true });
  };

  const handleCatalogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catalogFormData.name.trim()) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = getCatalogEndpoint(catalogDeviceType, catalogType);
      const payload: any = {
        name: catalogFormData.name.trim(),
        description: catalogFormData.description || null,
        isActive: catalogFormData.isActive
      };
      if (catalogDeviceType === 'xtam_video') {
        payload.versionHw = catalogFormData.versionHw || null;
        payload.versionSf = catalogFormData.versionSf || null;
        payload.so = catalogFormData.so || null;
      }
      if (editingCatalogItem) {
        await axios.put(`${API_URL}${endpoint}/${editingCatalogItem.id}`, payload, { headers });
        showSuccess('Actualizado correctamente');
      } else {
        await axios.post(`${API_URL}${endpoint}`, payload, { headers });
        showSuccess('Creado correctamente');
      }
      handleCloseCatalogModal();
      await loadCatalogItems();
      if (activeTab === catalogDeviceType || (catalogDeviceType === 'routers' && activeTab === 'routers') || (catalogDeviceType === 'sirenas' && activeTab === 'sirenas') || (catalogDeviceType === 'cameras' && activeTab === 'cameras') || (catalogDeviceType === 'buttons' && activeTab === 'buttons') || (catalogDeviceType === 'xtam_video' && activeTab === 'xtam_video') || (catalogDeviceType === 'miniot' && activeTab === 'miniot')) {
        if (catalogDeviceType === 'cameras') loadCameraReferences();
        else if (catalogDeviceType === 'buttons') loadButtonReferences();
        else if (catalogDeviceType === 'routers') loadRouterReferences();
        else if (catalogDeviceType === 'sirenas') loadSirenReferences();
        else if (catalogDeviceType === 'xtam_video') loadXtamVideoReferences();
        else if (catalogDeviceType === 'miniot') loadMiniotReferences();
      }
    } catch (error: any) {
      const msg = error.response?.data?.error?.message || error.response?.data?.message || error.message;
      showError(msg || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCatalog = (item: any) => {
    setCatalogItemToDelete(item);
    setShowConfirmDeleteCatalog(true);
  };

  const confirmDeleteCatalog = async () => {
    if (!catalogItemToDelete) return;
    setDeletingCatalog(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = getCatalogEndpoint(catalogDeviceType, catalogType);
      await axios.delete(`${API_URL}${endpoint}/${catalogItemToDelete.id}`, { headers });
      await loadCatalogItems();
      if (catalogDeviceType === 'cameras') loadCameraReferences();
      else if (catalogDeviceType === 'buttons') loadButtonReferences();
      else if (catalogDeviceType === 'routers') loadRouterReferences();
      else if (catalogDeviceType === 'sirenas') loadSirenReferences();
      else if (catalogDeviceType === 'xtam_video') loadXtamVideoReferences();
      else if (catalogDeviceType === 'miniot') loadMiniotReferences();
      setShowConfirmDeleteCatalog(false);
      setCatalogItemToDelete(null);
      showSuccess('Eliminado correctamente');
    } catch (error: any) {
      const msg = error.response?.data?.error?.message || error.response?.data?.message || error.message;
      showError(msg || 'Error al eliminar');
    } finally {
      setDeletingCatalog(false);
    }
  };

  const loadDevices = async (overrides?: { search?: string; filterBrand?: string; filterModel?: string; filterActive?: string }) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = getEndpoint(activeTab);
      const s = overrides?.search !== undefined ? overrides.search : search;
      const fb = overrides?.filterBrand !== undefined ? overrides.filterBrand : filterBrand;
      const fm = overrides?.filterModel !== undefined ? overrides.filterModel : filterModel;
      const fa = overrides?.filterActive !== undefined ? overrides.filterActive : filterActive;

      const params: Record<string, string | number> = {};
      if (s.trim()) params.search = s.trim();
      if (fa !== 'all') params.isActive = fa;
      if ((activeTab === 'cameras' || activeTab === 'buttons' || activeTab === 'routers' || activeTab === 'sirenas') && fb) params.idBrand = Number(fb);
      if ((activeTab === 'cameras' || activeTab === 'buttons' || activeTab === 'routers' || activeTab === 'sirenas' || activeTab === 'xtam_video' || activeTab === 'miniot') && fm) params.idModel = Number(fm);

      const response = await axios.get(`${API_URL}${endpoint}`, { headers, params });
      const loadedDevices = response.data.data || [];
      setDevices(loadedDevices);
      
      // Actualizar el contador de la pestaña activa
      setDeviceCounts(prev => ({
        ...prev,
        [activeTab]: loadedDevices.length
      }));
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
      setDeviceCounts(prev => ({
        ...prev,
        [activeTab]: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = (type: DeviceType): string => {
    const endpoints: Record<DeviceType, string> = {
      cameras: '/inventory/cameras',
      buttons: '/inventory/buttons',
      miniot: '/inventory/miniots',
      routers: '/inventory/routers',
      sirenas: '/inventory/sirens',
      xtam_video: '/inventory/xtam-videos',
      catalogos: '' // Catálogos no usa este endpoint; tiene su propia lógica
    };
    return endpoints[type];
  };

  const handleOpenModal = (device?: DeviceData) => {
    if (activeTab === 'routers') void loadRouterReferences();
    if (activeTab === 'sirenas') void loadSirenReferences();
    if (activeTab === 'xtam_video') void loadXtamVideoReferences();
    if (activeTab === 'miniot') void loadMiniotReferences();
    if (device) {
      setEditingDevice(device);
      // Transformar los datos del dispositivo al formato del formulario
      const deviceAny = device as any;
      
      if (activeTab === 'cameras') {
        setFormData({
          serial: deviceAny.serial || '',
          plate: deviceAny.plate || '',
          mac: deviceAny.mac || '',
          id_brand: deviceAny.id_brand || deviceAny.idBrand || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          isActive: deviceAny.isActive ?? true
        });
      } else if (activeTab === 'buttons') {
        setFormData({
          serial_device: deviceAny.serial_device || deviceAny.serialDevice || '',
          mac: deviceAny.mac || '',
          id_brand: deviceAny.id_brand || deviceAny.idBrand || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          description: deviceAny.description || '',
          code: deviceAny.code || '',
          inventory_state_id: deviceAny.inventory_state_id || deviceAny.inventoryStateId || 1,
          isActive: deviceAny.isActive ?? true
        });
      } else if (activeTab === 'miniot') {
        setFormData({
          serial_device: deviceAny.serial_device || deviceAny.serialDevice || '',
          mac: deviceAny.mac || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          firmware: deviceAny.firmware || '',
          inventory_state_id: deviceAny.inventory_state_id || deviceAny.inventoryStateId || 1,
          isActive: deviceAny.isActive ?? true
        });
      } else if (activeTab === 'routers') {
        setFormData({
          serial_device: deviceAny.serial_device || deviceAny.serialDevice || '',
          mac: deviceAny.mac || '',
          id_brand: deviceAny.id_brand || deviceAny.idBrand || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          inventory_state_id: deviceAny.inventory_state_id || deviceAny.inventoryStateId || 1,
          isActive: deviceAny.isActive ?? true
        });
      } else if (activeTab === 'sirenas') {
        setFormData({
          serial_device: deviceAny.serial_device || deviceAny.serialDevice || '',
          mac: deviceAny.mac || '',
          id_brand: deviceAny.id_brand || deviceAny.idBrand || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          inventory_state_id: deviceAny.inventory_state_id || deviceAny.inventoryStateId || 1,
          isActive: deviceAny.isActive ?? true
        });
      } else if (activeTab === 'xtam_video') {
        setFormData({
          serial_device: deviceAny.serial_device || deviceAny.serialDevice || '',
          mac: deviceAny.mac || '',
          id_model: deviceAny.id_model || deviceAny.idModel || '',
          inventory_state_id: deviceAny.inventory_state_id || deviceAny.inventoryStateId || 1,
          isActive: deviceAny.isActive ?? true
        });
      } else {
        setFormData(device);
      }
    } else {
      setEditingDevice(null);
      setFormData(getEmptyForm(activeTab));
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDevice(null);
    setFormData({});
  };

  const getEmptyForm = (type: DeviceType): any => {
    switch (type) {
      case 'cameras':
        return { serial: '', plate: '', mac: '', id_brand: '', id_model: '', isActive: true };
      case 'buttons':
        return { serial_device: '', mac: '', id_brand: '', id_model: '', description: '', code: '', inventory_state_id: 1, isActive: true };
      case 'routers':
        return { serial_device: '', mac: '', id_brand: '', id_model: '', inventory_state_id: 1, isActive: true };
      case 'sirenas':
        return { serial_device: '', mac: '', id_brand: '', id_model: '', inventory_state_id: 1, isActive: true };
      case 'miniot':
        return { serial_device: '', mac: '', id_model: '', firmware: '', inventory_state_id: 1, isActive: true };
      case 'xtam_video':
        return { 
          serial_device: '', 
          mac: '',
          id_model: '',
          inventory_state_id: 1,
          isActive: true
        };
      default:
        return {};
    }
  };

  const preparePayload = (data: any) => {
    const payload = { ...data };
    if (['buttons', 'routers', 'sirenas', 'xtam_video'].includes(activeTab)) {
      if (payload.id_brand === '' || Number.isNaN(payload.id_brand)) payload.id_brand = null;
      if (payload.id_model === '' || Number.isNaN(payload.id_model)) payload.id_model = null;
    }
    // XTAM Video: backend espera camelCase
    if (activeTab === 'xtam_video') {
      const p: Record<string, unknown> = {
        serialDevice: payload.serial_device,
        idModel: payload.id_model ?? null,
        mac: payload.mac,
        inventoryStateId: payload.inventory_state_id || null,
        isActive: payload.isActive ?? true
      };
      return p;
    }
    // MinIoT: backend espera camelCase
    if (activeTab === 'miniot') {
      const p: Record<string, unknown> = {
        serialDevice: payload.serial_device,
        idModel: payload.id_model ?? null,
        mac: payload.mac,
        firmware: payload.firmware || null,
        inventoryStateId: payload.inventory_state_id || null,
        isActive: payload.isActive ?? true
      };
      return p;
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      const endpoint = getEndpoint(activeTab);
      const isEditing = editingDevice && (editingDevice as any).id;
      const payload = preparePayload(formData);

      if (isEditing) {
        await axios.put(
          `${API_URL}${endpoint}/${(editingDevice as any).id}`,
          payload,
          { headers }
        );
      } else {
        await axios.post(`${API_URL}${endpoint}`, payload, { headers });
      }

      handleCloseModal();
      await loadDevices();
      await loadAllCounts();
      
      // Mostrar notificación de éxito
      switch (activeTab) {
        case 'cameras':
          isEditing ? notifications.cameras.updated() : notifications.cameras.created();
          break;
        case 'buttons':
          isEditing ? notifications.buttons.updated() : notifications.buttons.created();
          break;
        case 'miniot':
          isEditing ? notifications.miniot.updated() : notifications.miniot.created();
          break;
        case 'routers':
          isEditing ? notifications.routers.updated() : notifications.routers.created();
          break;
        case 'sirenas':
          isEditing ? notifications.sirenas.updated() : notifications.sirenas.created();
          break;
        case 'xtam_video':
          isEditing ? notifications.xtamVideo.updated() : notifications.xtamVideo.created();
          break;
      }
    } catch (error: any) {
      console.error('Error saving device:', error);
      const action = editingDevice ? 'actualizar' : 'crear';
      const err = error.response?.data?.error || error.response?.data;
      const detailMsg = err?.message || (Array.isArray(err?.details) ? err.details.map((d: { message?: string }) => d.message).filter(Boolean).join('. ') : null) || error.message;
      
      switch (activeTab) {
        case 'cameras':
          notifications.cameras.error(action, detailMsg);
          break;
        case 'buttons':
          notifications.buttons.error(action, detailMsg);
          break;
        case 'miniot':
          notifications.miniot.error(action, detailMsg);
          break;
        case 'routers':
          notifications.routers.error(action, detailMsg);
          break;
        case 'sirenas':
          notifications.sirenas.error(action, detailMsg);
          break;
        case 'xtam_video':
          notifications.xtamVideo.error(action, detailMsg);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (device: DeviceData) => {
    setDeviceToDelete(device);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;
    
    const deviceId = (deviceToDelete as any).id;
    if (!deviceId) return;

    setDeleting(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = getEndpoint(activeTab);
      await axios.delete(`${API_URL}${endpoint}/${deviceId}`, { headers });
      await loadDevices();
      await loadAllCounts();
      
      // Mostrar notificación de éxito
      switch (activeTab) {
        case 'cameras':
          notifications.cameras.deleted();
          break;
        case 'buttons':
          notifications.buttons.deleted();
          break;
        case 'miniot':
          notifications.miniot.deleted();
          break;
        case 'routers':
          notifications.routers.deleted();
          break;
        case 'sirenas':
          notifications.sirenas.deleted();
          break;
        case 'xtam_video':
          notifications.xtamVideo.deleted();
          break;
      }
      
      setShowConfirmDelete(false);
      setDeviceToDelete(null);
    } catch (error: any) {
      console.error('Error deleting device:', error);
      const err = error.response?.data?.error || error.response?.data;
      const detailMsg = err?.message || (Array.isArray(err?.details) ? err.details.map((d: { message?: string }) => d.message).filter(Boolean).join('. ') : null) || error.message;
      
      switch (activeTab) {
        case 'cameras':
          notifications.cameras.error('eliminar', detailMsg);
          break;
        case 'buttons':
          notifications.buttons.error('eliminar', detailMsg);
          break;
        case 'miniot':
          notifications.miniot.error('eliminar', detailMsg);
          break;
        case 'routers':
          notifications.routers.error('eliminar', detailMsg);
          break;
        case 'sirenas':
          notifications.sirenas.error('eliminar', detailMsg);
          break;
        case 'xtam_video':
          notifications.xtamVideo.error('eliminar', detailMsg);
          break;
      }
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setDeviceToDelete(null);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterBrand('');
    setFilterModel('');
    setFilterActive('all');
    void loadDevices({ search: '', filterBrand: '', filterModel: '', filterActive: 'all' });
  };

  const hasActiveFilters = search.trim() || filterBrand || filterModel || filterActive !== 'all';

  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case 'cameras':
        return 'Buscar por serial, placa, MAC, marca, modelo, sitio, IP de cámara...';
      case 'buttons':
        return 'Buscar por serial, MAC, código, descripción, marca, modelo, sitio...';
      case 'miniot':
        return 'Buscar por serial, MAC, firmware, modelo, sitio...';
      case 'routers':
        return 'Buscar por serial, MAC, marca, modelo, sitio...';
      case 'sirenas':
        return 'Buscar por serial, MAC, marca, modelo, sitio...';
      case 'xtam_video':
        return 'Buscar por serial, MAC, modelo, sitio, placas, referencias...';
      default:
        return 'Buscar...';
    }
  };

  const handleRefresh = async () => {
    await loadDevices();
    await loadAllCounts();
  };

  const getBrandName = (device: any) => {
    // El backend ahora devuelve brandName directamente
    if (device.brandName) return device.brandName;
    
    // Fallback: buscar en el array de brands por ID
    if (device.id_brand || device.idBrand) {
      const brandId = device.id_brand || device.idBrand;
      const brand = brands.find(b => b.id === brandId);
      return brand?.name || '-';
    }
    
    return '-';
  };

  const getModelName = (device: any) => {
    // El backend ahora devuelve modelName directamente
    if (device.modelName) return device.modelName;
    
    // Fallback: buscar en el array de models por ID
    if (device.id_model || device.idModel) {
      const modelId = device.id_model || device.idModel;
      const model = models.find(m => m.id === modelId);
      return model?.name || '-';
    }
    
    return '-';
  };

  const renderDeviceTable = () => {
    const getTableHeaders = () => {
      switch (activeTab) {
        case 'cameras':
          return ['Serial', 'Placa', 'MAC', 'Marca', 'Modelo', 'Estado', 'Sitio', 'Acciones'];
        case 'buttons':
          return ['Serial', 'MAC', 'Código', 'Marca', 'Modelo', 'Descripción', 'Estado', 'Sitio', 'Acciones'];
        case 'miniot':
          return ['Serial', 'MAC', 'Modelo', 'Firmware', 'Estado', 'Sitio', 'Acciones'];
        case 'routers':
          return ['Serial', 'MAC', 'Marca', 'Modelo', 'Estado', 'Sitio', 'Acciones'];
        case 'sirenas':
          return ['Serial', 'MAC', 'Marca', 'Modelo', 'Estado', 'Sitio', 'Acciones'];
        case 'xtam_video':
          return ['Serial', 'MAC', 'Modelo', 'Estado', 'Sitio', 'Acciones'];
        default:
          return ['Serial', 'Estado', 'Sitio', 'Acciones'];
      }
    };

    const renderTableRow = (device: DeviceData) => {
      const deviceAny = device as any;

      const getCells = () => {
        switch (activeTab) {
          case 'cameras':
            return (
              <>
                <td>{deviceAny.serial}</td>
                <td>{deviceAny.plate}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{getBrandName(deviceAny)}</td>
                <td>{getModelName(deviceAny)}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          case 'buttons':
            return (
              <>
                <td>{deviceAny.serial_device ?? deviceAny.serialDevice ?? '-'}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{deviceAny.code || '-'}</td>
                <td>{getBrandName(deviceAny)}</td>
                <td>{getModelName(deviceAny)}</td>
                <td className="description-cell">{deviceAny.description || '-'}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          case 'miniot':
            return (
              <>
                <td>{deviceAny.serial_device ?? deviceAny.serialDevice ?? '-'}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{getModelName(deviceAny)}</td>
                <td>{deviceAny.firmware || '-'}</td>
                <td>{deviceAny.version || '-'}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          case 'routers':
            return (
              <>
                <td>{deviceAny.serial_device ?? deviceAny.serialDevice ?? '-'}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{getBrandName(deviceAny)}</td>
                <td>{getModelName(deviceAny)}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          case 'sirenas':
            return (
              <>
                <td>{deviceAny.serial_device ?? deviceAny.serialDevice ?? '-'}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{getBrandName(deviceAny)}</td>
                <td>{getModelName(deviceAny)}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          case 'xtam_video':
            return (
              <>
                <td>{deviceAny.serial_device ?? deviceAny.serialDevice ?? '-'}</td>
                <td>{deviceAny.mac || '-'}</td>
                <td>{getModelName(deviceAny)}</td>
                <td>
                  <span className={`status-badge ${deviceAny.isActive ? 'active' : 'inactive'}`}>
                    {deviceAny.isActive ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
          default:
            return (
              <>
                <td>{deviceAny.serial_device || deviceAny.serial}</td>
                <td>
                  {deviceAny.siteName ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-green-500" size={16} />
                      <span className="text-sm text-green-300">{deviceAny.siteName}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Sin Sitio</span>
                  )}
                </td>
              </>
            );
        }
      };

      return (
        <tr key={deviceAny.id}>
          {getCells()}
          <td className="actions-cell">
            <div className="table-actions">
              <button
                onClick={() => handleOpenModal(device)}
                className="btn-icon btn-edit"
                title="Editar"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => handleDelete(device)}
                className="btn-icon btn-delete"
                title="Eliminar"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </td>
        </tr>
      );
    };

    return (
      <div className="table-container">
        <table className="devices-table">
          <thead>
            <tr>
              {getTableHeaders().map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map(renderTableRow)}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'cameras':
        return (
          <>
            <div className="form-group">
              <label>Serial *</label>
              <input
                type="text"
                value={formData.serial || ''}
                onChange={(e) => setFormData({ ...formData, serial: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Placa *</label>
              <input
                type="text"
                value={formData.plate || ''}
                onChange={(e) => setFormData({ ...formData, plate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>MAC *</label>
              <input
                type="text"
                value={formData.mac || ''}
                onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Marca *</label>
              <select
                value={formData.id_brand || ''}
                onChange={(e) => setFormData({ ...formData, id_brand: parseInt(e.target.value) })}
                required
              >
                <option value="">Seleccione una marca</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Modelo *</label>
              <select
                value={formData.id_model || ''}
                onChange={(e) => setFormData({ ...formData, id_model: parseInt(e.target.value) })}
                required
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive || false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      case 'buttons':
        return (
          <>
            <div className="form-group">
              <label>Serial del Dispositivo *</label>
              <input
                type="text"
                value={formData.serial_device || ''}
                onChange={(e) => setFormData({ ...formData, serial_device: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>MAC *</label>
              <input
                type="text"
                value={formData.mac || ''}
                onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Código</label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Marca</label>
              <select
                value={formData.id_brand || ''}
                onChange={(e) => setFormData({ ...formData, id_brand: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione una marca</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <select
                value={formData.id_model || ''}
                onChange={(e) => setFormData({ ...formData, id_model: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                rows={3}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      case 'routers':
        return (
          <>
            <div className="form-group">
              <label>Serial del Dispositivo *</label>
              <input
                type="text"
                value={formData.serial_device || ''}
                onChange={(e) => setFormData({ ...formData, serial_device: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>MAC *</label>
              <input
                type="text"
                value={formData.mac || ''}
                onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Marca</label>
              <select
                value={formData.id_brand ?? ''}
                onChange={(e) => setFormData({ ...formData, id_brand: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione una marca</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <select
                value={formData.id_model ?? ''}
                onChange={(e) => setFormData({ ...formData, id_model: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      case 'sirenas':
        return (
          <>
            <div className="form-group">
              <label>Serial del Dispositivo *</label>
              <input
                type="text"
                value={formData.serial_device || ''}
                onChange={(e) => setFormData({ ...formData, serial_device: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>MAC *</label>
              <input
                type="text"
                value={formData.mac || ''}
                onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Marca</label>
              <select
                value={formData.id_brand ?? ''}
                onChange={(e) => setFormData({ ...formData, id_brand: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione una marca</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <select
                value={formData.id_model ?? ''}
                onChange={(e) => setFormData({ ...formData, id_model: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      case 'miniot':
        return (
          <>
            <div className="form-group">
              <label>Serial del Dispositivo *</label>
              <input
                type="text"
                value={formData.serial_device || ''}
                onChange={(e) => setFormData({ ...formData, serial_device: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>MAC *</label>
              <input
                type="text"
                value={formData.mac || ''}
                onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <select
                value={formData.id_model ?? ''}
                onChange={(e) => setFormData({ ...formData, id_model: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Firmware</label>
              <input
                type="text"
                value={formData.firmware || ''}
                onChange={(e) => setFormData({ ...formData, firmware: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      case 'xtam_video':
        return (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Serial del Dispositivo *</label>
                <input
                  type="text"
                  value={formData.serial_device || ''}
                  onChange={(e) => setFormData({ ...formData, serial_device: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>MAC *</label>
                <input
                  type="text"
                  value={formData.mac || ''}
                  onChange={(e) => setFormData({ ...formData, mac: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Modelo</label>
              <select
                value={formData.id_model ?? ''}
                onChange={(e) => setFormData({ ...formData, id_model: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">Seleccione un modelo</option>
                {models.map(model => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Activo
              </label>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div className="header-title">
          <Package size={32} className="header-icon" />
          <div>
            <h1>Inventario de Dispositivos</h1>
            <p>Gestiona todos los dispositivos del sistema</p>
          </div>
        </div>
        <div className="header-actions">
          {activeTab !== 'catalogos' && (
            <>
              <button 
                onClick={handleRefresh} 
                className="btn-refresh"
                disabled={loading}
                title="Refrescar datos"
              >
                <RefreshCw size={20} className={loading ? 'spinning' : ''} />
                Refrescar
              </button>
              <button onClick={() => handleOpenModal()} className="btn-add">
                <Plus size={20} />
                Agregar Dispositivo
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={activeTab === tab.id ? { borderBottomColor: tab.color } : {}}
          >
            <tab.icon size={20} />
            <span>{tab.label}</span>
            {tab.id !== 'catalogos' && (
              <span className="tab-count">
                {deviceCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Contenido de Catálogos */}
      {activeTab === 'catalogos' && (
        <div className="catalog-section">
          <div className="catalog-selectors">
            <div className="form-group">
              <label>Tipo de dispositivo</label>
              <select
                className="inv-select catalog-select"
                value={catalogDeviceType}
                onChange={(e) => {
                  const val = e.target.value as CatalogDeviceType;
                  setCatalogDeviceType(val);
                  if (val === 'xtam_video' || val === 'miniot') setCatalogType('models');
                }}
              >
                <option value="cameras">Cámaras</option>
                <option value="buttons">Botones</option>
                <option value="routers">Routers</option>
                <option value="sirenas">Sirenas</option>
                <option value="xtam_video">XTAM Video</option>
                <option value="miniot">MinIoT</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tipo de catálogo</label>
              <select
                className="inv-select catalog-select"
                value={(catalogDeviceType === 'xtam_video' || catalogDeviceType === 'miniot') ? 'models' : catalogType}
                onChange={(e) => setCatalogType(e.target.value as CatalogType)}
                disabled={catalogDeviceType === 'xtam_video' || catalogDeviceType === 'miniot'}
              >
                <option value="brands">Marcas</option>
                <option value="models">Modelos</option>
              </select>
            </div>
            <button
              className="btn-add catalog-add-btn"
              onClick={() => handleOpenCatalogModal()}
            >
              <Plus size={20} />
              Agregar {(catalogDeviceType === 'xtam_video' || catalogDeviceType === 'miniot' || catalogType === 'models') ? 'modelo' : 'marca'}
            </button>
          </div>
          <div className="devices-container">
            {loading ? (
              <XTAMSpinner variant="inline" message="Cargando catálogos..." />
            ) : catalogItems.length === 0 ? (
              <div className="empty-state">
                <Tags size={64} />
                <h3>No hay {(catalogDeviceType === 'xtam_video' || catalogDeviceType === 'miniot' || catalogType === 'models') ? 'modelos' : 'marcas'} registrados</h3>
                <p>Agrega un {(catalogDeviceType === 'xtam_video' || catalogDeviceType === 'miniot' || catalogType === 'models') ? 'modelo' : 'marca'} usando el botón superior</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="devices-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      {catalogDeviceType === 'xtam_video' && (
                        <>
                          <th>Versión HW</th>
                          <th>Versión SW</th>
                          <th>SO</th>
                        </>
                      )}
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.name}</td>
                        <td className="description-cell">{item.description || '-'}</td>
                        {catalogDeviceType === 'xtam_video' && (
                          <>
                            <td>{item.versionHw || item.version_hw || '-'}</td>
                            <td>{item.versionSf || item.version_sf || '-'}</td>
                            <td>{item.so || '-'}</td>
                          </>
                        )}
                        <td>
                          <span className={`status-badge ${(item.isActive ?? item.is_active) ? 'active' : 'inactive'}`}>
                            {(item.isActive ?? item.is_active) ? 'activo' : 'inactivo'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <div className="table-actions">
                            <button
                              onClick={() => handleOpenCatalogModal(item)}
                              className="btn-icon btn-edit"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteCatalog(item)}
                              className="btn-icon btn-delete"
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filtros para todos los dispositivos */}
      {activeTab !== 'catalogos' && (
      <>
      <div className="inv-toolbar">
        <input
          className="inv-input"
          placeholder={getSearchPlaceholder()}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void loadDevices(); }}
          disabled={loading}
        />
        {(activeTab === 'cameras' || activeTab === 'buttons') && (
          <>
            <select
              className="inv-select"
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              disabled={loading}
            >
              <option value="">Todas las marcas</option>
              {brands.map((b) => (
                <option key={b.id} value={String(b.id)}>{b.name}</option>
              ))}
            </select>
            <select
              className="inv-select"
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              disabled={loading}
            >
              <option value="">Todos los modelos</option>
              {models.map((m) => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </select>
          </>
        )}
        {activeTab === 'xtam_video' && (
          <select
            className="inv-select"
            value={filterModel}
            onChange={(e) => setFilterModel(e.target.value)}
            disabled={loading}
          >
            <option value="">Todos los modelos</option>
            {models.map((m) => (
              <option key={m.id} value={String(m.id)}>{m.name}</option>
            ))}
          </select>
        )}
        {activeTab === 'miniot' && (
          <select
            className="inv-select"
            value={filterModel}
            onChange={(e) => setFilterModel(e.target.value)}
            disabled={loading}
          >
            <option value="">Todos los modelos</option>
            {models.map((m) => (
              <option key={m.id} value={String(m.id)}>{m.name}</option>
            ))}
          </select>
        )}
        <select
          className="inv-select"
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as 'all' | 'true' | 'false')}
          disabled={loading}
        >
          <option value="all">Todos</option>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
        </select>
        <button className="inv-btn secondary" onClick={() => void loadDevices()} disabled={loading}>
          Buscar
        </button>
        {hasActiveFilters && (
          <button className="inv-btn secondary" onClick={clearFilters} disabled={loading}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Lista de dispositivos */}
      <div className="devices-container">
        {loading ? (
          <XTAMSpinner variant="inline" message="Cargando dispositivos..." />
        ) : devices.length === 0 ? (
          <div className="empty-state">
            {currentTab && <currentTab.icon size={64} />}
            <h3>No hay {currentTab?.label.toLowerCase()} registrados</h3>
            <p>Agrega tu primer dispositivo usando el botón superior</p>
          </div>
        ) : (
          renderDeviceTable()
        )}
      </div>
      </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingDevice ? 'Editar' : 'Agregar'} {currentTab?.label}
              </h2>
              <button onClick={handleCloseModal} className="btn-close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {renderFormFields()}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de catálogo (marca/modelo) */}
      {showCatalogModal && (
        <div className="modal-overlay" onClick={handleCloseCatalogModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingCatalogItem ? 'Editar' : 'Agregar'} {catalogType === 'brands' ? 'marca' : 'modelo'} de {catalogDeviceType === 'cameras' ? 'cámara' : catalogDeviceType === 'buttons' ? 'botón' : catalogDeviceType === 'routers' ? 'router' : 'sirena'}
              </h2>
              <button onClick={handleCloseCatalogModal} className="btn-close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCatalogSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={catalogFormData.name}
                    onChange={(e) => setCatalogFormData({ ...catalogFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    rows={3}
                    value={catalogFormData.description || ''}
                    onChange={(e) => setCatalogFormData({ ...catalogFormData, description: e.target.value })}
                  />
                </div>
                {catalogDeviceType === 'xtam_video' && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Versión HW</label>
                        <input
                          type="text"
                          value={catalogFormData.versionHw || ''}
                          onChange={(e) => setCatalogFormData({ ...catalogFormData, versionHw: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Versión SW</label>
                        <input
                          type="text"
                          value={catalogFormData.versionSf || ''}
                          onChange={(e) => setCatalogFormData({ ...catalogFormData, versionSf: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Sistema Operativo</label>
                      <input
                        type="text"
                        value={catalogFormData.so || ''}
                        onChange={(e) => setCatalogFormData({ ...catalogFormData, so: e.target.value })}
                      />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={catalogFormData.isActive}
                      onChange={(e) => setCatalogFormData({ ...catalogFormData, isActive: e.target.checked })}
                    />
                    Activo
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={handleCloseCatalogModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación de dispositivo */}
      <ConfirmDialog
        isOpen={showConfirmDelete}
        title="¿Eliminar dispositivo?"
        message={`Esta acción eliminará permanentemente este ${currentTab?.label?.toLowerCase().slice(0, -1) || 'dispositivo'} del inventario. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleting}
      />

      {/* Modal de confirmación de eliminación de catálogo */}
      <ConfirmDialog
        isOpen={showConfirmDeleteCatalog}
        title="¿Eliminar?"
        message={`¿Eliminar esta ${catalogType === 'brands' ? 'marca' : 'modelo'}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        onConfirm={confirmDeleteCatalog}
        onCancel={() => { setShowConfirmDeleteCatalog(false); setCatalogItemToDelete(null); }}
        loading={deletingCatalog}
      />
    </div>
  );
};

export default InventoryPage;
